import { useMemo } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { SiteLevelBadge } from '@/components/SiteLevelBadge';
import { MiniprogramFeaturesCard } from '@/components/MiniprogramFeaturesCard';
import {
  buildFeaturesHierarchyGroups,
  effectiveSiteFeatures,
  featuresEnabledSummary,
  hasFeaturesOverride,
  type MiniproFeaturesSite,
} from '@/lib/miniproSiteFeatures';
import { defaultMiniproFeatures, normalizeMiniproFeatures, type MiniproFeatures } from '@/lib/miniproFeatures';

const detailsClass =
  'rounded-lg border border-slate-200 bg-white shadow-sm open:ring-1 open:ring-slate-200/80 [&_summary::-webkit-details-marker]:hidden';
const summaryClass =
  'flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-sm hover:bg-slate-50/80';

type Props = {
  sites: MiniproFeaturesSite[];
  siteIds: number[];
  globalFeatures: MiniproFeatures;
  sitePages: Record<string, MiniproFeatures>;
  miniprogramId: number;
  onSitePagesChange: (pages: Record<string, MiniproFeatures>) => void;
  onUpload: (file: File) => Promise<string>;
};

export function SiteMiniproFeaturesEditor({
  sites,
  siteIds,
  globalFeatures,
  sitePages,
  miniprogramId,
  onSitePagesChange,
  onUpload,
}: Props) {
  const global = globalFeatures || defaultMiniproFeatures();
  const pages = sitePages || {};
  const sitesById = useMemo(() => new Map(sites.map((s) => [s.id, s])), [sites]);
  const hierarchyGroups = useMemo(() => buildFeaturesHierarchyGroups(siteIds, sites), [siteIds, sites]);
  const scopeCount = useMemo(
    () => hierarchyGroups.reduce((n, g) => n + (g.parent ? 1 : 0) + g.children.length, 0),
    [hierarchyGroups],
  );

  function patchSite(siteId: number, next: MiniproFeatures) {
    onSitePagesChange({
      ...pages,
      [String(siteId)]: normalizeMiniproFeatures(next, { forEdit: true }),
    });
  }

  function startOverride(siteId: number) {
    const eff = effectiveSiteFeatures(global, pages, siteId, sitesById);
    patchSite(siteId, eff);
  }

  function clearOverride(siteId: number) {
    if (!hasFeaturesOverride(pages, siteId)) return;
    const next = { ...pages };
    delete next[String(siteId)];
    onSitePagesChange(next);
  }

  function renderSiteBlock(siteId: number, siteName: string, depth: 0 | 1, parentSiteName?: string) {
    const inheriting = depth === 1 && !hasFeaturesOverride(pages, siteId);
    const override = hasFeaturesOverride(pages, siteId);
    const eff = effectiveSiteFeatures(global, pages, siteId, sitesById);
    const summary = featuresEnabledSummary(eff);

    return (
      <details key={siteId} className={cn(detailsClass, depth === 1 && 'border-l-4 border-l-violet-500/35')}>
        <summary className={summaryClass}>
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <SiteLevelBadge depth={depth} className={depth === 1 ? '!px-1 !py-px !text-[9px]' : undefined} />
            <span className="truncate font-medium text-slate-900">{siteName}</span>
            {depth === 1 ? (
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                  inheriting ? 'bg-slate-100 text-slate-600' : 'bg-violet-50 text-violet-900 ring-1 ring-violet-200/80',
                )}
              >
                {inheriting ? '沿用一级' : '已单独配置'}
              </span>
            ) : override ? (
              <span className="shrink-0 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-900 ring-1 ring-violet-200/80">
                已单独配置
              </span>
            ) : (
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                沿用全站默认
              </span>
            )}
          </span>
          <span className="shrink-0 text-xs text-slate-500">{summary}</span>
          <span className="shrink-0 text-xs font-normal text-teal-700">展开</span>
        </summary>
        <div className="border-t border-slate-100 px-3 pb-4 pt-2">
          {depth === 1 && inheriting && parentSiteName ? (
            <p className="mb-3 rounded-lg border border-violet-200/80 bg-violet-50/90 px-3 py-2 text-xs leading-relaxed text-violet-950">
              当前沿用一级站点「{parentSiteName}」的运营扩展。点击下方「启用本站单独配置」后可覆盖；也可「恢复沿用一级」。
            </p>
          ) : null}
          {depth === 0 && !override ? (
            <p className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              未单独配置时，本站（及其下二级站点）使用上方「全站默认」设置。
            </p>
          ) : null}
          <div className="mb-3 flex flex-wrap justify-end gap-2">
            {!override ? (
              <Button type="button" variant="secondary" size="sm" onClick={() => startOverride(siteId)}>
                启用本站单独配置
              </Button>
            ) : (
              <Button type="button" variant="ghost" size="sm" className="!text-teal-800" onClick={() => clearOverride(siteId)}>
                {depth === 1 ? '恢复沿用一级' : '恢复沿用全站默认'}
              </Button>
            )}
          </div>
          {override ? (
            <MiniprogramFeaturesCard
              embedded
              features={eff}
              sites={sites}
              miniprogramId={miniprogramId}
              onChange={(next) => patchSite(siteId, next)}
              onUpload={onUpload}
            />
          ) : (
            <p className="text-xs text-slate-500">生效中：{summary}</p>
          )}
        </div>
      </details>
    );
  }

  if (siteIds.length === 0) {
    return <p className="text-sm text-slate-500">请先在上方选择展示站点并保存。</p>;
  }

  if (scopeCount === 0) {
    return <p className="text-sm text-slate-500">展示站点下暂无可配置的站点。</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs text-slate-600">
        <span>
          共 <strong className="text-slate-800">{scopeCount}</strong> 个站点；二级默认沿用所属一级，一级默认沿用全站。
        </span>
      </div>
      {hierarchyGroups.map((g, gi) => {
        const parentSite = g.parent?.site;
        const parentId = parentSite?.id ?? 0;
        const parentName = parentSite?.site_name ?? '';
        const childCount = g.children.length;

        if (parentSite && parentId > 0) {
          if (childCount === 0) {
            return renderSiteBlock(parentId, parentSite.site_name, 0);
          }
          const parentEff = effectiveSiteFeatures(global, pages, parentId, sitesById);
          return (
            <details key={`fg-${gi}-${parentId}`} className={detailsClass}>
              <summary className={cn(summaryClass, 'bg-slate-50/60')}>
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <SiteLevelBadge depth={0} />
                  <span className="truncate font-semibold text-slate-900">{parentSite.site_name}</span>
                  <span className="shrink-0 rounded-full bg-slate-200/80 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                    含 {childCount} 个二级
                  </span>
                </span>
                <span className="shrink-0 text-xs text-slate-500">{featuresEnabledSummary(parentEff)}</span>
                <span className="shrink-0 text-xs font-normal text-teal-700">展开</span>
              </summary>
              <div className="space-y-3 border-t border-slate-100 p-3">
                <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
                  <p className="mb-2 text-xs font-semibold text-slate-800">一级站点（本组默认，二级默认沿用）</p>
                  {!hasFeaturesOverride(pages, parentId) ? (
                    <div className="mb-3 flex justify-end">
                      <Button type="button" variant="secondary" size="sm" onClick={() => startOverride(parentId)}>
                        启用一级单独配置
                      </Button>
                    </div>
                  ) : (
                    <div className="mb-3 flex justify-end">
                      <Button type="button" variant="ghost" size="sm" className="!text-teal-800" onClick={() => clearOverride(parentId)}>
                        恢复沿用全站默认
                      </Button>
                    </div>
                  )}
                  {hasFeaturesOverride(pages, parentId) ? (
                    <MiniprogramFeaturesCard
                      embedded
                      features={parentEff}
                      sites={sites}
                      miniprogramId={miniprogramId}
                      onChange={(next) => patchSite(parentId, next)}
                      onUpload={onUpload}
                    />
                  ) : (
                    <p className="text-xs text-slate-500">生效中：{featuresEnabledSummary(parentEff)}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">二级站点</p>
                  {g.children.map((c) => renderSiteBlock(c.site.id, c.site.site_name, 1, parentName))}
                </div>
              </div>
            </details>
          );
        }

        return (
          <div key={`fg-orphan-${gi}`} className="space-y-2">
            {g.children.map((c) => renderSiteBlock(c.site.id, c.site.site_name, 1))}
          </div>
        );
      })}
    </div>
  );
}
